/**
 * Voice notes client (#3829).
 *
 *  - `GET  /api/v1/support/voice/status/`     -- signed-in; `{enabled, max_seconds?, max_bytes?}`.
 *  - `POST /api/v1/support/voice/transcribe/` -- signed-in; multipart `audio` + `duration`
 *    (seconds) → `{text}`. The audio is never stored server-side.
 *
 * The status answer is cached for the page's lifetime (one fetch, shared by the
 * help chat and the feedback form). A failed status fetch hides the mic.
 */
import { ApiError, apiRequest } from "./client";

export interface VoiceStatus {
  enabled: boolean;
  max_seconds?: number;
  max_bytes?: number;
}

const DISABLED: VoiceStatus = { enabled: false };

let statusPromise: Promise<VoiceStatus> | null = null;

export function getVoiceStatus(): Promise<VoiceStatus> {
  if (!statusPromise) {
    statusPromise = apiRequest<VoiceStatus>("/support/voice/status/").catch(() => {
      // Let a later mount retry (e.g. after sign-in), but answer "off" now.
      statusPromise = null;
      return DISABLED;
    });
  }
  return statusPromise;
}

/** Test hook: forget the cached status. */
export function resetVoiceStatusCache(): void {
  statusPromise = null;
}

const GENERIC_ERROR = "Couldn't turn that voice note into text. Please try again.";

/** A plain-language message for a failed transcription (never a vendor name). */
export function voiceErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return "Too many voice notes in a row. Please wait a moment and try again.";
    const detail = err.data?.detail;
    if (typeof detail === "string" && detail) return detail;
  }
  return GENERIC_ERROR;
}

export async function transcribeVoice(
  audio: Blob,
  durationSeconds: number,
  signal?: AbortSignal,
): Promise<string> {
  const ext = audio.type.includes("mp4") ? "m4a" : audio.type.includes("ogg") ? "ogg" : "webm";
  const body = new FormData();
  body.append("audio", audio, `voice-note.${ext}`);
  body.append("duration", durationSeconds.toFixed(1));
  const res = await apiRequest<{ text: string }>(
    "/support/voice/transcribe/",
    { method: "POST", body },
    signal,
  );
  return (res.text ?? "").trim();
}
